import * as React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import ErrorBanner from "../../components/ErrorBanner";
import { process } from "@progress/kendo-data-query";
import AlertDialog from "../../components/AlertDialog";

import { Loader } from "@progress/kendo-react-indicators";

import {
  useQueryGetModelAvailablePlans,
  useMutateModelAddPlan,
} from "../../data/services/clientService";
import ClientRMDeletePlanModal from "./ClientRMDeletePlanModal";

const OFFER_TYPE_TITLES = {
  MED: "Medical",
  DEN: "Dental",
  VIS: "Vision",
};

const OFFER_TYPE_ORDER = {
  MED: 1,
  DEN: 2,
  VIS: 3,
};

const getOfferTypeTitle = (offerTypeCode) => {
  return OFFER_TYPE_TITLES[offerTypeCode] ?? offerTypeCode;
};

const getSortedOfferTypes = (offerTypes) => {
  return [...offerTypes].sort((left, right) => {
    return (OFFER_TYPE_ORDER[left] ?? Number.MAX_SAFE_INTEGER) -
      (OFFER_TYPE_ORDER[right] ?? Number.MAX_SAFE_INTEGER) ||
      left.localeCompare(right);
  });
};

const buildPlansByOfferType = (plans) => {
  return plans.reduce((accumulator, plan) => {
    const offerTypeCode = plan.offerTypeCode ?? "UNKNOWN";

    if (!accumulator[offerTypeCode]) {
      accumulator[offerTypeCode] = [];
    }

    accumulator[offerTypeCode].push(plan);
    return accumulator;
  }, {});
};

const ClientRateModelAddPlan = ({
  oePeriodId,
  clientId,
  versionId,
  groupId,
  onClose,
}) => {
  const DATA_ITEM_KEY = "planId";
  const SELECTED_FIELD = "isAlreadyMapped";
  const gridSelectableSetting = {
    enabled: true,
    drag: false,
    cell: false,
    mode: "multiple",
  };

  const [selected, setSelected] = React.useState(0);
  const [submitted, setSubmitted] = React.useState(false);
  const [plansByOfferType, setPlansByOfferType] = React.useState({});
  const [gridStateByOfferType, setGridStateByOfferType] = React.useState({});

  const [showDeletePlanModal, setShowDeletePlanModal] = React.useState(false);
  const [showAlertDialog, setShowAlertDialog] = React.useState(false);
  const [plansToMapTo, setPlansToMapTo] = React.useState([]);
  const [currentDeleteItem, setCurrentDeleteItem] = React.useState(null);
  const [deletedPlans, setDeletedPlans] = React.useState([]);

  const offerTypes = React.useMemo(() => {
    return getSortedOfferTypes(Object.keys(plansByOfferType));
  }, [plansByOfferType]);

  const { isLoading: planListLoading, data: plansList } =
    useQueryGetModelAvailablePlans(oePeriodId, clientId, versionId, groupId);
  const useMutateModelAddPlanHandler = useMutateModelAddPlan();

  React.useEffect(() => {
    if (!planListLoading && plansList && plansList.data) {
      const normalizedPlans = plansList.data.plans.map((e) => {
        e.originalMapped = e.isAlreadyMapped;
        e.planDispName = `${e.planId} - ${e.planName}`;
        return e;
      });

      const nextPlansByOfferType = buildPlansByOfferType(normalizedPlans);
      const nextOfferTypes = getSortedOfferTypes(Object.keys(nextPlansByOfferType));

      setPlansByOfferType(nextPlansByOfferType);
      setGridStateByOfferType((currentState) => {
        return nextOfferTypes.reduce((nextState, offerTypeCode) => {
          nextState[offerTypeCode] = currentState[offerTypeCode] ?? {};
          return nextState;
        }, {});
      });
      setSelected((currentSelected) =>
        currentSelected < nextOfferTypes.length ? currentSelected : 0
      );
    }
  }, [planListLoading, plansList]);

  const handleSubmit = async () => {
    const plans = Object.values(plansByOfferType)
      .flat()
      .filter((plan) => plan.isAlreadyMapped === true)
      .map((plan) => plan.planId);

    let data = {
      oePeriodId: oePeriodId,
      clientId: clientId,
      modelVersionId: versionId,
      groupId: groupId,
      planIds: plans,
      deleteMovePlans: deletedPlans,
    };

    await useMutateModelAddPlanHandler.mutateAsync(data);

    onClose();
  };

  const handleTabSelect = (e) => {
    setSelected(e.selected);
  };

  const updatePlansForOfferType = (offerTypeCode, updatePlan) => {
    setPlansByOfferType((currentPlansByOfferType) => {
      return {
        ...currentPlansByOfferType,
        [offerTypeCode]: (currentPlansByOfferType[offerTypeCode] ?? []).map(
          updatePlan
        ),
      };
    });
  };

  const onMedicalSelectionChange = (event) => {
    const checkboxElement = event.syntheticEvent.target;
    const checked = checkboxElement.checked;
    const offerTypeCode = event.dataItem.offerTypeCode;

    if (
      checked ||
      !event.dataItem.originalMapped ||
      (event.dataItem.eeCount === 0 &&
        !deletedPlans.find((e) => e.moveToPlanId === event.dataItem.planId))
    ) {
      updatePlansForOfferType(offerTypeCode, (plan) => {
          if (plan.planId === event.dataItem.planId) {
            plan.isAlreadyMapped = checked;
          }
          return plan;
        });
    } else {
      const availablePlans = (plansByOfferType[offerTypeCode] ?? []).filter(
        (plan) => plan.planId !== event.dataItem.planId && plan.isAlreadyMapped
      );

      if (availablePlans.length === 0) {
        setShowAlertDialog(true);
        return;
      }

      setPlansToMapTo(availablePlans);
      setCurrentDeleteItem(event.dataItem);
      setShowDeletePlanModal(true);
    }
  };

  const handleDeleteConfirm = (item) => {
    updatePlansForOfferType(currentDeleteItem.offerTypeCode, (plan) => {
        if (plan.planId === currentDeleteItem.planId) {
          plan.isAlreadyMapped = false;
        }
        return plan;
      });

    let tempDelPlans = [...deletedPlans];
    let findDelItem = tempDelPlans.find(
      (e) => e.planId === currentDeleteItem.planId
    );
    if (findDelItem) {
      findDelItem.moveToPlanId = item.planId;
    } else {
      tempDelPlans.push({
        planId: currentDeleteItem.planId,
        moveToPlanId: item.planId,
      });
    }

    tempDelPlans = tempDelPlans.map((e) => {
      if (e.moveToPlanId === currentDeleteItem.planId) {
        e.moveToPlanId = item.planId;
      }
      return e;
    });

    setDeletedPlans(tempDelPlans);
    setShowDeletePlanModal(false);
    setPlansToMapTo([]);
    setCurrentDeleteItem(null);
  };

  // SOF-838: the backend only returns a carrier-terminated plan when the client already has
  // it (so it can be terminated); it is never offered to add. Label it so it is obvious why
  // the plan is here and why it should not be kept.
  const planNameCell = (props) => (
    <td>
      {props.dataItem.planDispName}
      {props.dataItem.isCarrierTerminated && (
        <span
          title="This plan is on the Carrier Terminated Plans list. It can only be terminated, not added."
          style={{
            marginLeft: 8,
            fontSize: 11,
            padding: "1px 8px",
            borderRadius: 10,
            backgroundColor: "#e0e0e0",
            color: "#666",
            whiteSpace: "nowrap",
          }}
        >
          Carrier Terminated Plan
        </span>
      )}
    </td>
  );

  const handleGridDataStateChange = (offerTypeCode, event) => {
    setGridStateByOfferType((currentState) => {
      return {
        ...currentState,
        [offerTypeCode]: event.dataState,
      };
    });
  };

  return (
    <Dialog title={"Manage Plans"} onClose={onClose} width={"90vw"}>
      <ErrorBanner
        handler={useMutateModelAddPlanHandler}
        onError={() => setSubmitted(false)}
      />
      {showDeletePlanModal && (
        <ClientRMDeletePlanModal
          plans={plansToMapTo}
          handleSubmit={handleDeleteConfirm}
          onClose={() => setShowDeletePlanModal(false)}
        />
      )}
      {showAlertDialog && (
        <AlertDialog
          title={"Can't delete"}
          message={
            "We can't delete last plan in this class. Please add plans before removing."
          }
          onClose={() => setShowAlertDialog(false)}
        />
      )}
      <TabStrip selected={selected} onSelect={handleTabSelect}>
        {offerTypes.map((offerTypeCode) => {
          const plans = plansByOfferType[offerTypeCode] ?? [];
          const gridState = gridStateByOfferType[offerTypeCode] ?? {};

          return (
            <TabStripTab key={offerTypeCode} title={getOfferTypeTitle(offerTypeCode)}>
              {planListLoading ? (
                <Loader type={"infinite-spinner"} />
              ) : (
                <Grid
                  data={process(plans, gridState)}
                  dataItemKey={DATA_ITEM_KEY}
                  selectedField={SELECTED_FIELD}
                  selectable={gridSelectableSetting}
                  onSelectionChange={onMedicalSelectionChange}
                  style={{ maxHeight: "50vh" }}
                  sortable={true}
                  onDataStateChange={(event) =>
                    handleGridDataStateChange(offerTypeCode, event)
                  }
                  {...gridState}
                >
                  <GridColumn
                    field={SELECTED_FIELD}
                    width="50px"
                    headerCell={() => ""}
                    headerSelectionValue={false}
                  />
                  <GridColumn
                    field="planDispName"
                    title="Plan Name"
                    cell={planNameCell}
                  />
                  <GridColumn
                    field="eeRate"
                    title="Employee Rate"
                    className="k-text-center"
                    format="{0:c}"
                  />
                  <GridColumn
                    field="esRate"
                    title="Spouse Rate"
                    className="k-text-center"
                    format="{0:c}"
                  />
                  <GridColumn
                    field="ecRate"
                    title="Single Parent Rate"
                    className="k-text-center"
                    format="{0:c}"
                  />
                  <GridColumn
                    field="famRate"
                    title="Family Rate"
                    className="k-text-center"
                    format="{0:c}"
                  />
                </Grid>
              )}
            </TabStripTab>
          );
        })}
      </TabStrip>
      <DialogActionsBar layout={"end"}>
        <div style={{ paddingRight: "8px", paddingBottom: "8px" }}>
          <Button onClick={onClose} style={{ marginRight: "10px" }}>
            {"Cancel"}
          </Button>
          <Button
            type={"submit"}
            themeColor={"primary"}
            disabled={submitted}
            onClick={() => handleSubmit()}
          >
            {"Save Changes"}
          </Button>
        </div>
      </DialogActionsBar>
    </Dialog>
  );
};

export default ClientRateModelAddPlan;
